import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@apollo/client';
import { imageUploadURL, Paths } from '../../url_helpers';
import { RCSegmentedControl, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import CMSSearchInputGroup from '../../components/cms_search_input_group';
import classNames from 'classnames';
import SanitizedRender from '../../components/sanitized_render';
import { withGraphql } from '../../with_graphql';
import { reverb_personalization_TrendingSearch, SiteBannerTrendingSearches } from '../../rql';
import { ChildProps } from '@apollo/client/react/hoc';
import TrendingSvg from '@reverbdotcom/cadence/icons/svgs/trending-up-arrow.svg?component';
import CoreLink from '../../components/core_link';
import isMobile from '../../is-mobile';
import { MParticleEventName } from '../../elog/mparticle_types';
import { trackEvent } from '../../elog/mparticle_tracker';
import { formatExperimentsForEventAttributes } from '../../elog/mparticle';
import { useUser } from '../../user_hooks';
import experiments from '../../experiments';
import { IUser } from '../../components/user_context_provider';

interface SiteBannerData {
  toggleName?: string;
  heading: string;
  subheading?: string;
  image: string;
  backgroundImage: string;
  secondaryImage: string;
  inputPlaceholderText?: string;
  displayDynamicContent?: boolean;
  pillOne?: string;
  pillOneHtml?: string;
  pillTwo?: string;
  pillTwoHtml?: string;
  pillThree?: string;
  pillThreeHtml?: string;
}

export interface IProps {
  siteBanners: SiteBannerData[];
}

interface ToggledSiteBannerControlProps {
  siteBanners: SiteBannerData[];
  onClick: (string) => void;
  enabledSiteBannerToggleName: string;
  user: Partial<IUser>
}

interface ToggledSiteBannerPillsProps {
  siteBanner: SiteBannerData;
  trendingSearches: reverb_personalization_TrendingSearch[]
  user: Partial<IUser>
}

function ToggledSiteBannerControl(props: ToggledSiteBannerControlProps) {
  if (props?.siteBanners?.length <= 1) { return null; }

  function clickEvent(siteBanner) {
    props.onClick(siteBanner.toggleName);
    trackEvent({
      componentName: 'ToggledSiteBannerControl',
      eventName: MParticleEventName.ClickedSiteBannerControl,
      experiments: formatExperimentsForEventAttributes(props.user, [experiments.LOGGED_OUT_HOMEPAGE_REDESIGN]),
    });
  }

  return (
    <RCSegmentedControl highContrast size={isMobile ? 'small' : 'medium'}>
      { props.siteBanners.map(siteBanner => {
        return (
          <RCSegmentedControl.Button
            key={siteBanner.toggleName}
            selected={siteBanner.toggleName === props.enabledSiteBannerToggleName}
            onClick={() => clickEvent(siteBanner)}
          >
            {siteBanner.toggleName}
          </RCSegmentedControl.Button>
        );
      })}
    </RCSegmentedControl>
  );
}

function ToggledSiteBannerPills(props: ToggledSiteBannerPillsProps) {
  if (props.siteBanner.displayDynamicContent && !props.trendingSearches.length) {
    return null;
  }

  const pills = [
    props.siteBanner.pillOneHtml || props.siteBanner.pillOne,
    props.siteBanner.pillTwoHtml || props.siteBanner.pillTwo,
    props.siteBanner.pillThreeHtml || props.siteBanner.pillThree,
  ];

  const clickEvent = (search) => {
    return {
      componentName: 'ToggledSiteBannerTrendingSearch',
      eventName: MParticleEventName.ClickedTrendingSearchPill,
      query: search?.query,
      productType: search?.categorySlug,
      experiments: formatExperimentsForEventAttributes(props.user, [experiments.LOGGED_OUT_HOMEPAGE_REDESIGN]),
    };
  };

  return (
    <div className="toggled-site-banner__pills">
      { props.siteBanner.displayDynamicContent ?
        <>
          { props.trendingSearches.map((search) => {
            return (
              <CoreLink
                key={`toggled-site-banner-dynamic-${search.query}`}
                className="toggled-site-banner__pill-dynamic"
                to={Paths.marketplace.expand({
                  query: search?.query,
                  product_type: search?.categorySlug,
                  referer: 'trending_search',
                })}
                clickEvent={clickEvent(search)}
              >
                <RCTextWithIcon
                  svgComponent={TrendingSvg}
                  placement="left"
                >
                  {search.query}
                </RCTextWithIcon>
              </CoreLink>
            );
          })}
        </>
        :
        <>
          { pills.map((pill, index) => {
            return (
              <SanitizedRender
                key={`toggled-site-banner-pill-${index}`}
                htmlTag="span"
                className="toggled-site-banner__pill-text"
                html={pill}
                inlineTagsOnly
              />
            );
          })}
        </>
      }
    </div>
  );
}

function ToggledSiteBanner(props: ChildProps<IProps, SiteBannerTrendingSearches.Query>) {
  const [enabledSiteBanner, setEnableSiteBanner] = React.useState(props?.siteBanners[0]);
  const user = useUser();
  if (!props?.siteBanners?.length) { return null; }

  const toggleControlOnClick = (toggleName) => {
    if (!toggleName) { return; }
    const enabledBanner = props.siteBanners.find(banner => banner.toggleName === toggleName);
    if (enabledBanner === enabledSiteBanner) { return; }
    setEnableSiteBanner(enabledBanner);
  };

  const index = props.siteBanners.findIndex(banner => banner.toggleName === enabledSiteBanner.toggleName);
  return (
    <div className="toggled-site-banner"
      style={{ backgroundImage: `url(${imageUploadURL(enabledSiteBanner.backgroundImage)})` }}
    >
      <div className="toggled-site-banner__inner" >
        <div className="toggled-site-banner__mobile-image" >
          <div className="image-box">
            <img
              src={imageUploadURL(enabledSiteBanner.secondaryImage)} alt=""
            />
          </div>
        </div>
        <div className="toggled-site-banner__content">
          <ToggledSiteBannerControl
            siteBanners={props.siteBanners}
            enabledSiteBannerToggleName={enabledSiteBanner.toggleName}
            onClick={toggleControlOnClick}
            user={user}
          />
          <p className="toggled-site-banner__subheading">
            { enabledSiteBanner.subheading }
          </p>
          <h2 className="toggled-site-banner__heading">
            { enabledSiteBanner.heading }
          </h2>
          <div className="toggled-site-banner__search-input">
            <CMSSearchInputGroup
              actionUrl={Paths.marketplace.expand({})}
              placeholder={enabledSiteBanner.inputPlaceholderText || I18n.t('commons.toggledSiteBanner.searchInputPlaceholder')}
              trackSearchFn={(query) => trackEvent({
                componentName: 'ToggledSiteBannerSearchInput',
                eventName: MParticleEventName.SiteSearch,
                query: query,
                experiments: formatExperimentsForEventAttributes(user, [experiments.LOGGED_OUT_HOMEPAGE_REDESIGN]),
              })}
              rounded
            />
          </div>
          <ToggledSiteBannerPills
            siteBanner={enabledSiteBanner}
            trendingSearches={props.data?.trendingSearches?.trendingSearches || []}
            user={user}
          />
        </div>
        <div className="toggled-site-banner__image-container">
          <div className="image-box">
            <img
              src={imageUploadURL(enabledSiteBanner.image)} alt=""
              className={classNames(
                { 'toggled-site-banner__image-first': index === 0 },
                { 'toggled-site-banner__image-second': index !== 0 },
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export const TRENDING_SEARCHES_QUERY = gql`query SiteBannerTrendingSearches(
  $limit: Int
) {
  trendingSearches(input: {
    nResults: $limit,
  }) {
    trendingSearches {
      query
      categorySlug
    }
  }
}`;

const connect = withGraphql<IProps, SiteBannerTrendingSearches.Query, SiteBannerTrendingSearches.Variables>(
  TRENDING_SEARCHES_QUERY,
  {
    skip: ownProps => ownProps.siteBanners.every(sb => !sb.displayDynamicContent),
    options: {
      ssr: false,
      variables: {
        limit: 3,
      },
    },
  },
);

export default connect(ToggledSiteBanner);
